import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Form, useForm } from 'react-hook-form';
import axios from 'axios';
import { AlertCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { config } from '@/config';
import { AuthShell } from './AuthShell';

interface FormInputs {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  signupToken: string;
}

interface SignupStatus {
  allowPublicSignups: boolean;
  invitation: {
    email: string;
    isValid: boolean;
    isExpired: boolean;
    isAccepted: boolean;
  } | null;
}

export default function AccountSignUp() {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({ delayError: 750 });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [submitError, setSubmitError] = useState('');
  const [signupStatus, setSignupStatus] = useState<SignupStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistWebsite, setWaitlistWebsite] = useState(''); // honeypot
  const [waitlistState, setWaitlistState] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [waitlistError, setWaitlistError] = useState('');

  const joinWaitlist = async (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(waitlistEmail)) {
      setWaitlistError('Please enter a valid email address.');
      return;
    }
    setWaitlistError('');
    setWaitlistState('submitting');
    try {
      await axios.post(`${config.backendApi}/api/auth/waitlist`, {
        email: waitlistEmail,
        website: waitlistWebsite,
      });
      setWaitlistState('done');
    } catch (err) {
      setWaitlistState('idle');
      setWaitlistError(
        axios.isAxiosError(err) && err.response?.status === 429
          ? 'Too many requests — please try again later.'
          : 'Something went wrong. Please try again.',
      );
    }
  };

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const params = token ? { token } : {};
        const response = await axios.get<SignupStatus>(
          `${config.backendApi}/api/auth/signup-status`,
          { params },
        );
        setSignupStatus(response.data);
        if (response.data.invitation?.email) {
          setValue('email', response.data.invitation.email);
        }
        if (token) {
          setValue('signupToken', token);
        }
      } catch {
        setSignupStatus({ allowPublicSignups: true, invitation: null });
      } finally {
        setStatusLoading(false);
      }
    };
    void checkStatus();
  }, [token, setValue]);

  if (statusLoading) {
    return (
      <AuthShell title="Sign up">
        <div className="p-6 flex flex-col items-center text-center">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        </div>
      </AuthShell>
    );
  }

  const inviteOnly = signupStatus && !signupStatus.allowPublicSignups;
  const hasValidToken = signupStatus?.invitation?.isValid === true;

  if (inviteOnly && !hasValidToken) {
    return (
      <AuthShell
        title="Sign up"
        subtitle="Account creation is by invitation only."
        width="md"
      >
        <div className="p-6 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-muted-foreground mb-4">
            <Mail className="w-5 h-5" />
          </div>
          {!token ? (
            <>
              <p className="text-sm text-foreground">
                If you've received a signup invitation, use the link provided in your invitation.
              </p>
              <div className="w-full border-t border-border my-5" />
              {waitlistState === 'done' ? (
                <p className="text-sm text-foreground">
                  You're on the list! We'll email you an invitation when a spot opens up.
                </p>
              ) : (
                <form onSubmit={(e) => { void joinWaitlist(e); }} className="w-full space-y-3">
                  <p className="text-sm text-muted-foreground">
                    No invitation yet? Join the waitlist and we'll email you one when a spot
                    opens up.
                  </p>
                  {/* Honeypot: hidden from real users, filled by bots. */}
                  <input
                    type="text"
                    name="website"
                    value={waitlistWebsite}
                    onChange={(e) => { setWaitlistWebsite(e.target.value); }}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      value={waitlistEmail}
                      onChange={(e) => { setWaitlistEmail(e.target.value); }}
                      aria-label="Email for waitlist"
                    />
                    <Button type="submit" disabled={waitlistState === 'submitting'}>
                      {waitlistState === 'submitting' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Join waitlist'
                      )}
                    </Button>
                  </div>
                  {waitlistError ? (
                    <p className="text-xs text-destructive text-left">{waitlistError}</p>
                  ) : null}
                </form>
              )}
            </>
          ) : signupStatus.invitation?.isExpired ? (
            <p className="text-sm text-destructive">
              This signup invitation has expired. Contact your administrator for a new invitation.
            </p>
          ) : signupStatus.invitation?.isAccepted ? (
            <p className="text-sm text-foreground">
              This invitation has already been used. If you already have an account,{' '}
              <Link to="/signin" className="text-primary hover:underline font-medium">
                sign in
              </Link>
              .
            </p>
          ) : (
            <p className="text-sm text-destructive">
              Invalid signup invitation. Check your invitation link or contact your administrator.
            </p>
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create your account"
      width="md"
      footer={
        <>
          Already have an account?{' '}
          <Link to="/signin" className="text-primary hover:underline font-medium">
            Sign in
          </Link>
        </>
      }
    >
      <Form
        method="post"
        action={`${config.backendApi}/api/auth/register`}
        control={control}
        encType="application/json"
        onSuccess={() => { void navigate('/account/mustverify'); }}
        onError={() => { setSubmitError('An unknown error occurred. Please try again.'); }}
        className="p-6 space-y-4"
      >
        <input type="hidden" {...register('signupToken')} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="signup-firstName">First name</Label>
            <Input
              id="signup-firstName"
              type="text"
              autoComplete="given-name"
              {...register('firstName')}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="signup-lastName">Last name</Label>
            <Input
              id="signup-lastName"
              type="text"
              autoComplete="family-name"
              {...register('lastName')}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-email">Email</Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            readOnly={!!signupStatus?.invitation?.email}
            {...register('email', {
              required: 'required',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Entered value does not match email format',
              },
            })}
          />
          {errors.email ? (
            <p className="text-xs text-destructive">This email is invalid.</p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-password">Password</Label>
          <Input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            {...register('password', { required: 'required' })}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-confirm">Confirm password</Label>
          <Input
            id="signup-confirm"
            type="password"
            autoComplete="new-password"
            aria-invalid={!!errors.confirmPassword}
            {...register('confirmPassword', {
              required: true,
              validate: (val: string, values) =>
                values.password === val || 'Passwords do not match',
            })}
          />
          {errors.confirmPassword ? (
            <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
          ) : null}
        </div>

        {submitError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        ) : null}

        {/* Acceptance at the point of signup is what makes the Terms binding —
            a link in the footer is not agreement. Kept adjacent to the button
            rather than behind a checkbox: browsewrap is weak, and an unchecked
            box is a conversion tax for the same legal effect as this. */}
        <p className="text-xs text-muted-foreground">
          By creating an account you agree to our{' '}
          <a
            href="https://witinaai.com/terms"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-foreground"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="https://witinaai.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-foreground"
          >
            Privacy Policy
          </a>
          .
        </p>

        <Button type="submit" className="w-full">
          Create account
        </Button>
      </Form>
    </AuthShell>
  );
}
